import { useCallback, useContext, useEffect, useMemo, useState } from 'react'

import styled from '@emotion/styled'

import { Button, Flex } from '@cash-design-system/react'
import { Country } from '@cash-web/protos/squareup/common/countries.pb'
import { InstrumentType } from '@cash-web/protos/squareup/common/instrument.pb'
import { GlobalAddress } from '@cash-web/protos/squareup/common/location.pb'
import { CashInstrumentType } from '@cash-web/protos/squareup/franklin/cash_instrument_type.pb'
import logger from '@cash-web/shared/util-logger'
import { useMutationAuthorizeDigitalWalletPayment } from '@cash-web/shared-data-access-api/react-query'
import { ErrorContext } from '@cash-web/shared-data-access-error'
import { useLogCashWebCdfEvent } from '@cash-web/shared-feature-analytics'
import { useFlowContext } from '@cash-web/shared-flow-data-access-context'
import { getEnvironmentAtRunTime } from '@cash-web/shared-util-environment'
import { retryFetch } from '@cash-web/shared-util-fetch'
import { WebInteractClick, WebViewViewComponent } from '@squareup/customer-data-events-web'

import { SQUARE_PCI_MERCHANT_VALIDATION } from '../constants'
import { ValidMoney } from '../validity'
import { GooglePayLogo } from './GooglePayLogo'

const CDF_COMPONENT_NAME = 'Google Pay Button'

// 2.0 was the latest version at time of writing.
const GOOGLE_PAY_API_VERSION = {
  apiVersion: 2,
  apiVersionMinor: 0,
}

const INSTRUMENT_TYPE_TO_GOOGLE_PAY_NETWORK: Partial<Record<InstrumentType, google.payments.api.CardNetwork>> = {
  [InstrumentType.VISA]: 'VISA',
  [InstrumentType.MASTER_CARD]: 'MASTERCARD',
  [InstrumentType.AMERICAN_EXPRESS]: 'AMEX',
  [InstrumentType.DISCOVER]: 'DISCOVER',
}

interface GooglePayButtonProps {
  amount: ValidMoney
  cashInstrumentTypes: InstrumentType[]
  billingAddressRequired?: boolean
  phoneNumberRequired?: boolean
  emailRequired?: boolean
  shippingAddressRequired?: boolean
  supportedInstrumentTypes: CashInstrumentType[]
  allowableIssuerCountries: Country[]
  setSubmitting?: (submitting: boolean) => void
}

interface MerchantSessionResponse {
  token: string
}

const toGlobalAddress = (address?: google.payments.api.Address): GlobalAddress => {
  if (!address) {
    return {}
  }

  return {
    address_line_1: address.address1,
    address_line_2: address.address2,
    locality: address.locality,
    administrative_district_level_1: address.administrativeArea,
    postal_code: address.postalCode,
    country_code: address.countryCode as Country,
  }
}

const formatAmount = (amount: ValidMoney): string => {
  return (amount.amount / 100).toFixed(2).toString()
}

const useGooglePaymentsClient = (merchantInfo: google.payments.api.MerchantInfo) => {
  return useMemo(() => {
    return new google.payments.api.PaymentsClient({
      environment: getEnvironmentAtRunTime() === 'production' ? 'PRODUCTION' : 'TEST',
      merchantInfo,
    })
  }, [merchantInfo])
}

const useSupportedCardNetworks = (cashInstrumentTypes: InstrumentType[]) => {
  return useMemo(() => {
    const networks = cashInstrumentTypes
      .map(instrumentType => {
        const network = INSTRUMENT_TYPE_TO_GOOGLE_PAY_NETWORK[instrumentType]
        if (!network) {
          logger.warn(new Error(`Instrument type not found in Google Pay Network map: ${instrumentType}`))
        }
        return network
      })
      .filter(Boolean) as google.payments.api.CardNetwork[]

    if (!networks.length) {
      throw new Error('Google Pay: No supported networks found')
    }

    return networks
  }, [cashInstrumentTypes])
}

const useCardPaymentMethod = ({
  cashInstrumentTypes,
  supportedInstrumentTypes,
  billingAddressRequired,
  phoneNumberRequired,
  allowableIssuerCountries,
}: {
  cashInstrumentTypes: InstrumentType[]
  supportedInstrumentTypes: CashInstrumentType[]
  billingAddressRequired?: boolean
  phoneNumberRequired?: boolean
  allowableIssuerCountries: Country[]
}): google.payments.api.PaymentMethodSpecification => {
  const supportedCardNetworks = useSupportedCardNetworks(cashInstrumentTypes)

  const paymentMethodSpecification: google.payments.api.PaymentMethodSpecification = useMemo(() => {
    return {
      type: 'CARD',
      parameters: {
        allowedAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'],
        allowedCardNetworks: supportedCardNetworks,
        allowCreditCards: supportedInstrumentTypes.includes(CashInstrumentType.CREDIT_CARD),
        allowPrepaidCards: false,
        allowedIssuerCountryCodes: allowableIssuerCountries,
        billingAddressRequired,
        billingAddressParameters: {
          format: 'FULL',
          phoneNumberRequired,
        },
      },
      tokenizationSpecification: {
        type: 'PAYMENT_GATEWAY',
        parameters: {
          gateway: SQUARE_PCI_MERCHANT_VALIDATION.GATEWAY,
          gatewayMerchantId: SQUARE_PCI_MERCHANT_VALIDATION.MERCHANT_ID,
        },
      },
    }
  }, [
    supportedCardNetworks,
    supportedInstrumentTypes,
    billingAddressRequired,
    phoneNumberRequired,
    allowableIssuerCountries,
  ])

  return paymentMethodSpecification
}

const useGooglePayReadiness = (
  googlePaymentsClient: google.payments.api.PaymentsClient,
  cardPaymentMethod: google.payments.api.PaymentMethodSpecification
) => {
  const [isReadyToPay, setIsReadyToPay] = useState(false)

  useEffect(() => {
    const checkIfReadyToPay = async () => {
      try {
        const isReadyToPay = await googlePaymentsClient.isReadyToPay({
          apiVersion: GOOGLE_PAY_API_VERSION.apiVersion,
          apiVersionMinor: GOOGLE_PAY_API_VERSION.apiVersionMinor,
          allowedPaymentMethods: [cardPaymentMethod],
        })
        setIsReadyToPay(isReadyToPay.result)
      } catch (error) {
        logger.error(new Error(`Error checking Google Pay readiness: ${error}`))
        setIsReadyToPay(false)
      }
    }

    checkIfReadyToPay()
  }, [cardPaymentMethod, googlePaymentsClient])

  return isReadyToPay
}

const useJwtToken = (): string | undefined => {
  const [jwtToken, setJwtToken] = useState<string>()

  useEffect(() => {
    const getJwtToken = async () => {
      try {
        const merchantSessionResponse = await retryFetch(SQUARE_PCI_MERCHANT_VALIDATION.GOOGLE_PAY_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            client_id: SQUARE_PCI_MERCHANT_VALIDATION.CLIENT_ID,
            location_id: SQUARE_PCI_MERCHANT_VALIDATION.LOCATION_ID,
            validation_url: `${window.location.origin}${window.location.pathname}`,
            merchant_name: SQUARE_PCI_MERCHANT_VALIDATION.MERCHANT_NAME,
          }),
        })

        const merchantSessionData = (await merchantSessionResponse.json()) as MerchantSessionResponse
        setJwtToken(merchantSessionData.token)
      } catch (err) {
        logger.error(new Error(`Error fetching merchant session: ${err}`))
      }
    }

    getJwtToken()
  }, [])

  return jwtToken
}

const usePaymentDataRequest = ({
  cardPaymentMethod,
  amount,
  emailRequired,
  shippingAddressRequired,
  jwtToken,
}: {
  cardPaymentMethod: google.payments.api.PaymentMethodSpecification
  amount: ValidMoney
  emailRequired?: boolean
  shippingAddressRequired?: boolean
  jwtToken?: string
}) => {
  const transactionInfo = useMemo(() => {
    const { allowCreditCards } = cardPaymentMethod.parameters

    // If credit cards are allowed, we want do not want to show the total price
    // in the sheet because we may apply a fee amount to the payment. We also
    // set the `checkoutOption` to `DEFAULT` which causes the submit button in the
    // GPay modal to show "Continue" instead of "Pay now".
    if (allowCreditCards) {
      return {
        totalPriceStatus: 'NOT_CURRENTLY_KNOWN',
        currencyCode: amount.currency_code,
        countryCode: 'US',
        checkoutOption: 'DEFAULT',
      }
    } else {
      return {
        totalPriceStatus: 'FINAL',
        totalPriceLabel: 'Total',
        totalPrice: formatAmount(amount),
        currencyCode: amount.currency_code,
        countryCode: 'US',
        checkoutOption: 'COMPLETE_IMMEDIATE_PURCHASE',
      }
    }
  }, [amount, cardPaymentMethod.parameters])

  if (!jwtToken) return null

  return {
    apiVersion: GOOGLE_PAY_API_VERSION.apiVersion,
    apiVersionMinor: GOOGLE_PAY_API_VERSION.apiVersionMinor,
    allowedPaymentMethods: [cardPaymentMethod],
    merchantInfo: {
      authJwt: jwtToken,
      merchantId: SQUARE_PCI_MERCHANT_VALIDATION.MERCHANT_ID,
      merchantName: SQUARE_PCI_MERCHANT_VALIDATION.MERCHANT_NAME,
    },
    transactionInfo,
    emailRequired,
    shippingAddressRequired,
  } as google.payments.api.PaymentDataRequest
}

// Styled components
const ButtonHeightSameAsApplePayButton = styled(Button)`
  height: 3.25rem;

  > span {
    height: 3.25rem;
    max-height: 3.25rem;
  }
`

const ButtonContent = styled.span`
  height: 1.5rem;

  > svg {
    height: 100%;
    width: auto;
  }
`

export const GooglePayButton = ({
  amount,
  cashInstrumentTypes,
  billingAddressRequired,
  phoneNumberRequired,
  emailRequired,
  shippingAddressRequired,
  supportedInstrumentTypes,
  setSubmitting,
  allowableIssuerCountries,
}: GooglePayButtonProps) => {
  const logCdfEvent = useLogCashWebCdfEvent()
  const { mutateAsync: authorizeDigitalWalletPayment } = useMutationAuthorizeDigitalWalletPayment()
  const flow = useFlowContext()
  const { clearError } = useContext(ErrorContext)

  const jwtToken = useJwtToken()

  const googlePaymentsClient = useGooglePaymentsClient({
    merchantId: SQUARE_PCI_MERCHANT_VALIDATION.MERCHANT_ID,
    merchantName: SQUARE_PCI_MERCHANT_VALIDATION.MERCHANT_NAME,
  })
  const cardPaymentMethod = useCardPaymentMethod({
    cashInstrumentTypes,
    supportedInstrumentTypes,
    billingAddressRequired,
    phoneNumberRequired,
    allowableIssuerCountries,
  })
  const isReadyToPay = useGooglePayReadiness(googlePaymentsClient, cardPaymentMethod)
  const paymentDataRequest = usePaymentDataRequest({
    cardPaymentMethod,
    amount,
    emailRequired,
    shippingAddressRequired,
    jwtToken,
  })

  const handlePaymentClick = useCallback(async () => {
    clearError()

    if (!paymentDataRequest) {
      logger.error(new Error('Google Pay: No payment data request'))
      return
    }

    logCdfEvent(WebInteractClick({ componentName: CDF_COMPONENT_NAME }))

    try {
      const { paymentMethodData, email } = await googlePaymentsClient.loadPaymentData(paymentDataRequest)

      setSubmitting?.(true)

      await flow.updateWithResponse({
        response: authorizeDigitalWalletPayment({
          google_pay_payment_token: {
            token: paymentMethodData.tokenizationData.token,
            merchantId: SQUARE_PCI_MERCHANT_VALIDATION.MERCHANT_ID,
          },
          email,
          first_name: paymentMethodData.info?.billingAddress?.name,
          phone_number: paymentMethodData.info?.billingAddress?.phoneNumber,
          address: toGlobalAddress(paymentMethodData.info?.billingAddress),
        }),
        queriesToInvalidate: [],
      })
    } catch (error: unknown) {
      if (typeof error === 'object' && error !== null && 'statusCode' in error && error.statusCode === 'CANCELED') {
        // User closed the Google Pay sheet
        return
      }
      logger.error(new Error(`Error loading Google Pay: ${error}`))
    } finally {
      setSubmitting?.(false)
    }
  }, [
    flow,
    googlePaymentsClient,
    authorizeDigitalWalletPayment,
    paymentDataRequest,
    logCdfEvent,
    setSubmitting,
    clearError,
  ])

  useEffect(() => {
    logCdfEvent(WebViewViewComponent({ componentName: CDF_COMPONENT_NAME }))
  }, [logCdfEvent])

  if (!isReadyToPay) {
    return null
  }

  return (
    <ButtonHeightSameAsApplePayButton
      size="large"
      loading={!jwtToken}
      onClick={handlePaymentClick}
      data-testid="google-pay-button"
    >
      <Flex>
        <ButtonContent>
          <GooglePayLogo />
        </ButtonContent>
      </Flex>
    </ButtonHeightSameAsApplePayButton>
  )
}
